import React, { useState, useEffect } from 'react';
import { Card, Field, Input } from '../../../components/app2';

/**
 * Información personal del usuario (rediseño jul-2026).
 *
 * Mismos cinco campos y las mismas reglas: correo y nombre de usuario son de
 * solo lectura (los cambia el sistema, no la persona), y nombre, apellido y
 * teléfono se validan mientras se escriben.
 */

const UserProfileForm = ({ userData, profileData, onDataChange }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    username: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when userData or profileData changes
  useEffect(() => {
    if (userData) {
      const sourceData = profileData?.user || userData;
      setFormData({
        first_name: sourceData?.first_name || '',
        last_name: sourceData?.last_name || '',
        email: sourceData?.email || '',
        phone: sourceData?.phone || '',
        username: sourceData?.username || '',
      });
    }
  }, [userData, profileData]);

  // Validate individual fields
  const validateField = (field, value) => {
    let error = null;

    switch (field) {
      case 'phone': {
        const phoneRegex = /^[0-9]{10}$/;
        if (value && !phoneRegex?.test(value?.replace(/\s|-/g, ''))) {
          error = 'El teléfono debe tener 10 dígitos';
        }
        break;
      }

      case 'first_name':
      case 'last_name':
        if (value && value?.length < 2) {
          error = 'Debe tener al menos 2 caracteres';
        }
        break;

      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }

    return !error;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    // Don't allow changes to email or username (read-only fields)
    if (field === 'email' || field === 'username') {
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Validate field in real-time
    validateField(field, value);

    // Notify parent of changes with field and value
    onDataChange?.(field, value);
  };

  // Format phone number for display
  const formatPhoneNumber = (value) => {
    const cleaned = value?.replace(/\D/g, '');
    if (cleaned?.length === 10) {
      return `${cleaned?.slice(0, 2)} ${cleaned?.slice(2, 6)} ${cleaned?.slice(6, 10)}`;
    }
    return value;
  };

  return (
    <Card>
      <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Información personal</h3>
      <p className="a2-card-sub" style={{ marginBottom: 20 }}>
        Cómo apareces en la plataforma y cómo te contactamos
      </p>

      {/* Dos columnas como máximo: un formulario de datos personales se lee peor
          cuanto más ancho es cada renglón. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 }}>
        <Field label="Nombre *" htmlFor="first_name" error={errors?.first_name}>
          <Input
            id="first_name"
            type="text"
            value={formData?.first_name}
            onChange={(e) => handleInputChange('first_name', e?.target?.value)}
            placeholder="Ingresa tu nombre"
          />
        </Field>

        <Field label="Apellido *" htmlFor="last_name" error={errors?.last_name}>
          <Input
            id="last_name"
            type="text"
            value={formData?.last_name}
            onChange={(e) => handleInputChange('last_name', e?.target?.value)}
            placeholder="Ingresa tu apellido"
          />
        </Field>

        <Field
          label="Correo electrónico"
          htmlFor="email"
          hint="No se puede modificar: es tu identificador de acceso."
        >
          <Input id="email" type="email" value={formData?.email} readOnly disabled />
        </Field>

        <Field
          label="Teléfono"
          htmlFor="phone"
          error={errors?.phone}
          hint={errors?.phone ? undefined : 'Diez dígitos, sin lada internacional.'}
        >
          <Input
            id="phone"
            type="tel"
            value={formatPhoneNumber(formData?.phone)}
            onChange={(e) => handleInputChange('phone', e?.target?.value?.replace(/\s|-/g, ''))}
            placeholder="55 1234 5678"
            maxLength="12"
          />
        </Field>

        <Field
          label="Nombre de usuario"
          htmlFor="username"
          hint="No se puede modificar."
          style={{ gridColumn: '1 / -1' }}
        >
          <Input id="username" type="text" value={formData?.username} readOnly disabled />
        </Field>
      </div>
    </Card>
  );
};

export default UserProfileForm;
